/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable react/prop-types */
/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { FCM } from '@capacitor-community/fcm';
import { Device } from '@capacitor/device';
import { PushNotifications } from '@capacitor/push-notifications';
import { Preferences } from '@capacitor/preferences';
import {
  IonIcon, IonLabel, IonPage, IonRouterOutlet, IonSplitPane, IonTabBar, IonTabButton, IonTabs, isPlatform, setupIonicReact,
} from '@ionic/react';
/* Core CSS required for Ionic components to work properly */
import '@ionic/react/css/core.css';
import '@ionic/react/css/display.css';
import '@ionic/react/css/flex-utils.css';
import '@ionic/react/css/float-elements.css';
/* Basic CSS for apps built with Ionic */
import '@ionic/react/css/normalize.css';
/* Optional CSS utils that can be commented out */
import '@ionic/react/css/padding.css';
import '@ionic/react/css/structure.css';
import '@ionic/react/css/text-alignment.css';
import '@ionic/react/css/text-transformation.css';
import '@ionic/react/css/typography.css';
import React, { useCallback, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import {
  Redirect, Route, useHistory, useLocation,
} from 'react-router-dom';
import { Browser } from '@capacitor/browser';
import Admin from './apps/Admin';
import EmailVerify from './apps/Email/Verify';
import ChangePassword from './apps/Password/ChangePassword';
import Provider from './apps/Provider';
import Public from './apps/Public';
import Menu from './components/Menu';
import menuItems from './components/MenuItems';
import FirebaseData from './interfaces/FirebaseData';
import client from './lib/api/client';
import useIsDesktop from './lib/hooks/useIsDesktop';
import routePaths from './lib/routePaths';
import { useStoreActions, useStoreState } from './store';
import UpdateBanner from './components/UpdateBanner';
import StormAlertModal from './components/StormAlertModal';
import StormBanner from './components/StormBanner';
import { StormState } from './interfaces/Storm';
/* Theme variables */
import './theme/index.css';
import './theme/variables.css';

setupIonicReact();

const App: React.FC = () => {
  const history = useHistory();
  const location = useLocation();
  const [showToolbar, setShowToolbar] = useState(true);
  const [selectedPage, setSelectedPage] = useState('');
  const [registeredPushNotification, setRegisteredPushNotification] = useState(false);

  // // required to keep user info in sync via a computed property
  useStoreState((state) => state.analytics.user);
  const { getGeneralNotifications, getUrgentNotifications, stormTestMode } = useStoreState(state => state.settings);
  const findHotlines = useStoreActions((actions) => actions.crisisHotlines.find);
  const {
    auth: { reAuth, setError },
    settings: { loadSettings, setGetGeneralNotifications, setGetUrgentNotifications },
    meta: { get: metaGet },
    favorites: { loadFavoriteListings },
    translations: { setLocale, loadSavedLanguage },
    pages: { find: loadPages },
    analytics: { initialize, createEvent },
    storm: {
      load: loadStorm,
      loadContent: loadStormContent,
      applyPushPayload: applyStormPushPayload,
      incrementReopenSignal: signalStormReopen,
    },
  } = useStoreActions(
    ({
      auth, meta, settings, favorites, translations, pages, analytics, storm,
    }) => ({
      auth,
      meta,
      settings,
      favorites,
      translations,
      pages,
      analytics,
      storm,
    }),
  );

  const handleStormPushPayload = useCallback((data: { [k: string]: string }) => {
    if (!data || data.type !== 'hurricane_alert') return;
    applyStormPushPayload({
      state: (data.state as StormState) || 'none',
      state_version: parseInt(data.state_version || '0', 10),
      title_en: data.title_en || null,
      title_es: data.title_es || null,
      body_en: data.body_en || null,
      body_es: data.body_es || null,
      auto_clear_at: null,
      last_push_at: null,
      activated_at: null,
      updated_at: null,
    });
  }, [applyStormPushPayload]);

  const initializePushNotifications = useCallback(() => {
    PushNotifications.addListener('registration', () => {
      setRegisteredPushNotification(true);
    });
    PushNotifications.addListener('pushNotificationReceived', (notification) => {
      const { data } = notification;
      if (data?.type === 'hurricane_alert') {
        handleStormPushPayload(data);
      }
    });
    PushNotifications.addListener('pushNotificationActionPerformed', (notification) => {
      const { data } = notification.notification;
      if (data?.type === 'hurricane_alert') {
        handleStormPushPayload(data);
        if (data?.link) {
          Browser.open({ url: data.link });
        }
        return;
      }
      if (notification.actionId === 'tap' && data?.link) {
        Browser.open({ url: data.link });
      }
    });
    FCM.setAutoInit({ enabled: true });
    PushNotifications.register();
  }, [handleStormPushPayload]);

  useEffect(() => {
    if (getGeneralNotifications || getUrgentNotifications || stormTestMode) {
      PushNotifications.checkPermissions().then(res => {
        if (res.receive !== 'granted') {
          PushNotifications.requestPermissions().then(newRes => {
            if (newRes.receive === 'denied') {
              setGetGeneralNotifications(false);
              setGetUrgentNotifications(false);
            } else {
              initializePushNotifications();
            }
          });
        } else {
          initializePushNotifications();
        }
      });
    }
  }, [getGeneralNotifications, getUrgentNotifications, stormTestMode, initializePushNotifications, setGetGeneralNotifications, setGetUrgentNotifications]);

  useEffect(() => {
    if (registeredPushNotification) {
      if (getUrgentNotifications) {
        FCM.subscribeTo({ topic: 'urgent' });
      } else {
        FCM.unsubscribeFrom({ topic: 'urgent' });
      }

      if (getGeneralNotifications) {
        FCM.subscribeTo({ topic: 'general' });
      } else {
        FCM.unsubscribeFrom({ topic: 'general' });
      }

      // Emergency topic has no opt-out — life-safety messaging
      FCM.subscribeTo({ topic: 'emergency' });

      // Test storm push topic — opt-in via Settings → Storm test alerts.
      if (stormTestMode) {
        FCM.subscribeTo({ topic: 'emergency-test' });
      } else {
        FCM.unsubscribeFrom({ topic: 'emergency-test' });
      }
    }
  }, [getGeneralNotifications, getUrgentNotifications, stormTestMode, registeredPushNotification]);

  // Load current storm alert state and editable content on app mount.
  useEffect(() => {
    loadStorm();
    loadStormContent();
  }, [loadStorm, loadStormContent]);

  // When the app/tab becomes visible again, refresh alert + content and re-show modal.
  // For imminent state this means the popup returns every time the user opens the app.
  useEffect(() => {
    const onVisibility = (): void => {
      if (document.visibilityState === 'visible') {
        loadStorm();
        loadStormContent();
        signalStormReopen();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [loadStorm, loadStormContent, signalStormReopen]);

  useEffect(() => {
    if (
      location.pathname === routePaths.public.crisis
      || location.pathname === routePaths.public.onboarding
    ) {
      setShowToolbar(false);
    } else if (!showToolbar) {
      setShowToolbar(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  useEffect(() => {
    document.addEventListener('onFirebaseData', (data: unknown) => {
      const { route } = data as FirebaseData;
      history.push(route);
    });
  }, [history]);

  useEffect(() => {
    if (selectedPage !== location.pathname) {
      setSelectedPage(location.pathname);
    }
  }, [selectedPage, location.pathname]);

  useEffect(() => {
    const currentVersion = '0.0.25';
    (async (): Promise<void> => {
      const version = await Preferences.get({ key: 'version' });
      if (!version.value) {
        await Preferences.clear();
        await Preferences.set({ key: 'version', value: currentVersion });
        const lang = await Device.getLanguageCode();
        if (lang.value.startsWith('es')) {
          setLocale('es');
        }
      } else if (version.value !== currentVersion) {
        await Preferences.set({ key: 'version', value: currentVersion });
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    (async () => {
      const onboarded = await Preferences.get({ key: 'seeker_onboarded' });
      if (isPlatform('mobile') && onboarded) {
        if (getGeneralNotifications || getUrgentNotifications) {
          PushNotifications.checkPermissions().then(res => {
            if (res.receive !== 'granted') {
              PushNotifications.requestPermissions().then(newRes => {
                if (newRes.receive === 'denied') {
                  setGetGeneralNotifications(false);
                  setGetUrgentNotifications(false);
                }
              });
            } else {
              initializePushNotifications();
            }
          });
        }
      }
    })();
  }, [getGeneralNotifications, getUrgentNotifications, initializePushNotifications, setGetGeneralNotifications, setGetUrgentNotifications]);

  useEffect(() => {
    (async (): Promise<void> => {
      await metaGet();
      await loadFavoriteListings();
      await loadSettings();
      await loadSavedLanguage();
      await loadPages();
      await findHotlines();
      reAuth();
      await initialize();
      await createEvent({
        type: 'app-load',
        meta: {},
      });
    })();
  }, [reAuth, metaGet, loadFavoriteListings, loadSettings, loadSavedLanguage, loadPages, initialize, createEvent, findHotlines]);

  useEffect(() => {
    client.hooks({
      error: [
        (context): void => {
          if (
            context.error.message.includes('jwt')
            || context.error.message.includes('auth')
          ) {
            setError('Session expired.');
            history.push(routePaths.provider.signin);
          }
          throw context.error;
        },
      ],
    });
  }, [history, setError]);

  const isDesktop = useIsDesktop();
  const [resizeCount, setResizeCount] = useState(0);
  useEffect(() => {
    setResizeCount((prev) => prev + 1);
  }, [isDesktop]);

  if (location.pathname.startsWith(routePaths.email.root)) {
    return (
      <IonRouterOutlet>
        <Route
          exact
          path={routePaths.email.verifyToken}
          component={EmailVerify}
        />
        <Route exact path={routePaths.email.verify} component={EmailVerify} />
        <Redirect to={routePaths.email.verify} />
      </IonRouterOutlet>
    );
  }

  if (location.pathname.startsWith(routePaths.password.root)) {
    return (
      <IonRouterOutlet>
        <Route
          exact
          path={routePaths.password.resetToken}
          component={ChangePassword}
        />
        <Route
          exact
          path={routePaths.password.reset}
          component={ChangePassword}
        />
        <Redirect to={routePaths.password.reset} />
      </IonRouterOutlet>
    );
  }

  // Desktop users seeing admin do not see split pane.
  if (isDesktop && location.pathname.startsWith(routePaths.admin.root)) {
    return (
      <IonRouterOutlet>
        <Route path={routePaths.admin.root} component={Admin} />
      </IonRouterOutlet>
    );
  }

  // Mobile users seeing admin do see "split pane" in that they have a menu that loads other content.

  return (
    <>
      <StormAlertModal />
      <div
        className="storm-app-shell"
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <StormBanner />
        <div style={{ flex: 1, minHeight: 0, position: 'relative' }}>
          {isPlatform('ios') || isPlatform('android') ? <UpdateBanner /> : null}
          {isDesktop
        || location.pathname.startsWith(routePaths.admin.root)
        || location.pathname.startsWith(routePaths.provider.approved.root) ? (
          <IonSplitPane
            contentId="desktopMain"
            when={isDesktop ? 'sm' : '(min-width: 880px)'}
          >
            <Menu selectedPage={selectedPage} />
            <div
              id="desktopMain"
              style={{
                width: resizeCount > 1 ? '100%' : undefined,
                position: resizeCount > 1 ? 'relative' : undefined,
              }}
            >
              <IonRouterOutlet>
                <Route path={routePaths.provider.root} component={Provider} />
                <Route path={routePaths.public.root} component={Public} />
                <Route path={routePaths.admin.root} component={Admin} />
                <Redirect to={routePaths.public.root} />
              </IonRouterOutlet>
            </div>
          </IonSplitPane>
            ) : (
              <IonSplitPane contentId="mobileMain" when="(min-width: 880px)">
                <Menu selectedPage={selectedPage} />
                <IonPage id="mobileMain">
                  <IonTabs>
                    <IonRouterOutlet>
                      <Route path={routePaths.provider.root} component={Provider} />
                      <Route path={routePaths.public.root} component={Public} />
                      <Route path={routePaths.admin.root} component={Admin} />
                      <Redirect to={routePaths.public.root} />
                    </IonRouterOutlet>
                    <IonTabBar hidden={!showToolbar} slot="bottom">
                      {menuItems
                        .filter((menuItem) => menuItem.tabInfo)
                        .sort(
                          (a, b) => {
                            if (a.tabInfo && b.tabInfo) {
                              return a.tabInfo.tabOrderIndex - b.tabInfo.tabOrderIndex;
                            }
                            return 1;
                          },
                        )
                        .map((tabItem) => (
                          <IonTabButton
                            selected={selectedPage === tabItem.url}
                            key={tabItem.tabInfo?.tabTitleId}
                            tab={tabItem.tabInfo?.tabTitleId}
                            href={tabItem.url}
                          >
                            <IonIcon icon={tabItem.icon} />
                            <IonLabel className="ion-text-wrap">
                              <FormattedMessage id={tabItem.tabInfo?.tabTitleId} />
                            </IonLabel>
                          </IonTabButton>
                        ))}
                    </IonTabBar>
                  </IonTabs>
                </IonPage>
              </IonSplitPane>
            )}
        </div>
      </div>
    </>
  );
};

export default App;
